import { useEffect, useState } from "react";
import { QrCode, Copy, Download, Mail, Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
};

// Jun 2026 Partner Platform Phase 3 — referral link/QR + commercial
// agreement controls inside the admin PartnerDetail modal.
export default function PartnerReferralSection({ partner }) {
  return (
    <>
      <ReferralLinkCard partner={partner} />
      <AgreementCard partner={partner} />
    </>
  );
}

function ReferralLinkCard({ partner }) {
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    // Skip the fetch entirely when the capability is explicitly off —
    // avoids a guaranteed 400 in the console for non-referral partners.
    if (partner.capabilities && partner.capabilities.can_refer === false) {
      setBlocked(true);
      return;
    }
    api.get(`/admin/partners/${partner.id}/referral-link`)
      .then(({ data }) => { setData(data); setBlocked(false); })
      .catch((e) => setBlocked(e.response?.status === 400));
  }, [partner.id, partner.capabilities]);

  const emailIt = async () => {
    setEmailing(true);
    try {
      const { data: r } = await api.post(`/admin/partners/${partner.id}/referral-link/email`);
      r.ok ? toast.success("Referral link & QR emailed to partner") : toast.error("Email failed to send");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setEmailing(false); }
  };

  if (blocked) {
    return (
      <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="partner-referral-section">
        <div className="flex items-center gap-2">
          <QrCode className="w-4 h-4 text-cyan-600" />
          <h3 className="font-display font-semibold text-slate-900">Referral Link & QR</h3>
        </div>
        <p className="mt-2 text-xs text-slate-500" data-testid="partner-referral-disabled-note">
          Enable the <span className="font-semibold">Referral Partner</span> capability above to
          generate this partner's unique tracked link and QR code.
        </p>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="partner-referral-section">
      <div className="flex items-center gap-2">
        <QrCode className="w-4 h-4 text-cyan-600" />
        <h3 className="font-display font-semibold text-slate-900">Referral Link & QR</h3>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Bookings made through this link/QR are attributed to the partner and earn commission
        under their agreement. {data.stats.visits} visit{data.stats.visits !== 1 ? "s" : ""} ·{" "}
        {data.stats.attributed_referrals} attributed booking{data.stats.attributed_referrals !== 1 ? "s" : ""}.
      </p>
      <div className="mt-3 flex items-start gap-4">
        <img src={data.qr_data_url} alt="Referral QR code" data-testid="partner-referral-qr"
          className="w-28 h-28 rounded-xl border border-slate-200" />
        <div className="flex-1 min-w-0">
          <Input readOnly value={data.url} data-testid="partner-referral-url" className="h-9 rounded-xl text-xs" />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full" data-testid="partner-referral-copy"
              onClick={async () => {
                (await copyText(data.url)) ? toast.success("Link copied") : toast.error("Copy failed — select the link manually");
              }}>
              <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
            </Button>
            <Button size="sm" variant="outline" className="rounded-full" asChild data-testid="partner-referral-download">
              <a href={data.qr_data_url} download={`aware-referral-qr-${data.slug}.png`}>
                <Download className="w-3.5 h-3.5 mr-1.5" /> Download QR
              </a>
            </Button>
            <Button size="sm" onClick={emailIt} disabled={emailing} data-testid="partner-referral-email"
              className="btn-aqua rounded-full font-semibold">
              {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Mail className="w-3.5 h-3.5 mr-1.5" /> Email to partner</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

const KIND_LABELS = { water_test: "Water test bookings", membership: "Memberships" };

function AgreementCard({ partner }) {
  const [data, setData] = useState(null);
  const [pct, setPct] = useState({});
  const [wholesale, setWholesale] = useState({});
  const [kitCredit, setKitCredit] = useState("");
  const [kitWholesalePrice, setKitWholesalePrice] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get(`/admin/partners/${partner.id}/commercial-agreement`).then(({ data }) => {
      setData(data);
      const p = {};
      for (const k of Object.keys(KIND_LABELS)) {
        const v = data.agreement.rates?.[k];
        p[k] = v != null ? String(Math.round(v * 10000) / 100) : "";
      }
      setPct(p);
      const w = {};
      for (const t of data.tests || []) {
        const c = data.retail?.wholesale_cents?.[t.id];
        w[t.id] = c != null ? String((c / 100).toFixed(2)) : "";
      }
      setWholesale(w);
      setKitCredit(data.retail?.kit_credit_cents ? String((data.retail.kit_credit_cents / 100).toFixed(2)) : "");
      setKitWholesalePrice(data.retail?.kit_wholesale_cents != null ? String((data.retail.kit_wholesale_cents / 100).toFixed(2)) : "");
      setNotes(data.agreement.notes || "");
    }).catch(() => {});
  useEffect(() => { load(); }, [partner.id]);

  const save = async () => {
    const rates = {};
    for (const [k, raw] of Object.entries(pct)) {
      if (raw === "" || raw == null) { rates[k] = null; continue; }
      const v = parseFloat(raw);
      if (Number.isNaN(v) || v < 0 || v > 100) return toast.error(`${KIND_LABELS[k]}: enter 0–100%`);
      rates[k] = Math.round(v * 100) / 10000;
    }
    const retail_wholesale = {};
    for (const [tid, raw] of Object.entries(wholesale)) {
      if (raw === "" || raw == null) { retail_wholesale[tid] = null; continue; }
      const v = parseFloat(raw);
      if (Number.isNaN(v) || v < 0) return toast.error("Wholesale prices must be positive dollars");
      retail_wholesale[tid] = Math.round(v * 100);
    }
    let retail_kit_credit_cents = 0;
    if (kitCredit !== "") {
      const v = parseFloat(kitCredit);
      if (Number.isNaN(v) || v < 0) return toast.error("Kit credit must be a positive dollar amount");
      retail_kit_credit_cents = Math.round(v * 100);
    }
    let retail_kit_wholesale_cents = 0;
    if (kitWholesalePrice !== "") {
      const v = parseFloat(kitWholesalePrice);
      if (Number.isNaN(v) || v < 0) return toast.error("Kit price must be a positive dollar amount");
      retail_kit_wholesale_cents = Math.round(v * 100);
    }
    setSaving(true);
    try {
      await api.put(`/admin/partners/${partner.id}/commercial-agreement`,
        { rates, notes, retail_wholesale, retail_kit_credit_cents, retail_kit_wholesale_cents });
      toast.success("Commercial agreement saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save agreement");
    } finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <div className="mt-5 rounded-2xl border border-slate-200 p-4" data-testid="partner-agreement-section">
      <div className="flex items-center gap-2">
        <Handshake className="w-4 h-4 text-cyan-600" />
        <h3 className="font-display font-semibold text-slate-900">Commercial Agreement</h3>
      </div>
      <p className="mt-1 text-[11px] text-slate-500">
        Per-partner commission rates applied by the existing commission engine to this partner's
        attributed bookings. Leave blank to use the platform default. Changes are audited.
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {Object.entries(KIND_LABELS).map(([k, label]) => (
          <div key={k}>
            <Label className="text-[11px] font-semibold text-slate-600">
              {label} <span className="text-slate-400 font-normal">(default {(data.defaults[k] * 100).toFixed(0)}%)</span>
            </Label>
            <div className="mt-1 relative">
              <Input data-testid={`agreement-rate-${k}`} value={pct[k] ?? ""} inputMode="decimal"
                onChange={(e) => setPct({ ...pct, [k]: e.target.value })}
                placeholder={`${(data.defaults[k] * 100).toFixed(0)}`}
                className="h-9 rounded-xl pr-8 text-sm" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
            </div>
          </div>
        ))}
      </div>
      {partner.capabilities?.can_retail && (
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Retail Stockist wholesale terms</div>
          <p className="mt-0.5 text-[11px] text-slate-500">
            Per-test wholesale price invoiced to the partner for counter bookings ($AUD).
            Blank = test not offered. Confidential — never shown to customers.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {(data.tests || []).map((t) => (
              <div key={t.id}>
                <Label className="text-[11px] font-semibold text-slate-600">{t.name}</Label>
                <div className="mt-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                  <Input data-testid={`agreement-wholesale-${t.id}`} value={wholesale[t.id] ?? ""} inputMode="decimal"
                    onChange={(e) => setWholesale({ ...wholesale, [t.id]: e.target.value })}
                    className="h-9 rounded-xl pl-7 text-sm" />
                </div>
              </div>
            ))}
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Kit credit per own-stock kit</Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                <Input data-testid="agreement-kit-credit" value={kitCredit} inputMode="decimal"
                  onChange={(e) => setKitCredit(e.target.value)}
                  className="h-9 rounded-xl pl-7 text-sm" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold text-slate-600">Kit reorder price (per kit)</Label>
              <div className="mt-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                <Input data-testid="agreement-kit-wholesale" value={kitWholesalePrice} inputMode="decimal"
                  onChange={(e) => setKitWholesalePrice(e.target.value)}
                  className="h-9 rounded-xl pl-7 text-sm" />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <Label className="text-[11px] font-semibold text-slate-600">Agreement notes (internal)</Label>
        <Textarea data-testid="agreement-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={2} className="mt-1 rounded-xl text-sm" placeholder="e.g. 12% water tests agreed 15 Jun 2026, review Dec 2026" />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button size="sm" onClick={save} disabled={saving} data-testid="agreement-save"
          className="btn-aqua rounded-full font-semibold">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save agreement"}
        </Button>
        {data.updated_at && (
          <span className="text-[11px] text-slate-400" data-testid="agreement-updated-note">
            Updated {new Date(data.updated_at).toLocaleDateString("en-AU")}{data.updated_by ? ` by ${data.updated_by}` : ""}
          </span>
        )}
      </div>
    </div>
  );
}
